import type { UserCatalogDto } from '@/api/generator';
import { mdiPlus, mdiStamper } from '@mdi/js';
import { bin, edit } from '@/components/icons/icons';
import { Nullable } from '@/utils/helper';

const EntityChangeFallbackName = 'Ptw';

type HistoryType = 'created' | 'deleted' | 'modified' | 'ptwCreated' | 'registered';

type HistoryChangeUsageCase = 'created' | 'modified' | 'editing' | 'completed' | 'registered';

type EntityChangeType = HistoryType | HistoryChangeUsageCase;

export interface EntityChangeEvent {
  user: string;
  time: string;
  type: EntityChangeType;
}

export const entityChangeIcons: Record<EntityChangeType, string | undefined> = {
  created: mdiPlus,
  ptwCreated: mdiPlus,
  modified: edit,
  deleted: bin,
  registered: mdiStamper,
  editing: undefined,
  completed: undefined,
};

interface HasCreatedEntityChangeEvent {
  creatorUser?: UserCatalogDto;
  creationTime: string;
}

function isHasCreatedEntityChangeEvent(
  changeEvent: MayHaveAnyEntityChangeEvent,
): changeEvent is HasCreatedEntityChangeEvent {
  return !!changeEvent.creationTime;
}

function changeEventFromCreated(
  changeEvent: MayHaveAnyEntityChangeEvent,
): EntityChangeEvent | undefined {
  return isHasCreatedEntityChangeEvent(changeEvent)
    ? {
        user: changeEvent.creatorUser?.fullName ?? EntityChangeFallbackName,
        time: changeEvent.creationTime,
        type: changeEvent.creatorUser?.fullName ? 'created' : 'ptwCreated',
      }
    : undefined;
}

interface HasDeletionEntityChangeEvent {
  deletionUser: UserCatalogDto;
  deletionTime: string;
}

function isHasDeletionEntityChangeEvent(
  changeEvent: MayHaveAnyEntityChangeEvent,
): changeEvent is HasDeletionEntityChangeEvent {
  return !!changeEvent.deletionUser && !!changeEvent.deletionTime;
}

function changeEventFromDeletion(
  changeEvent: MayHaveAnyEntityChangeEvent,
): EntityChangeEvent | undefined {
  return isHasDeletionEntityChangeEvent(changeEvent)
    ? {
        user: changeEvent.deletionUser.fullName,
        time: changeEvent.deletionTime,
        type: 'deleted',
      }
    : undefined;
}

interface HasLastModificationEntityChangeEvent {
  lastModifierUser: UserCatalogDto;
  lastModificationTime: string;
}

function isMayHaveLastModifcationEntityChangeEvent(
  changeEvent: MayHaveAnyEntityChangeEvent,
): changeEvent is HasLastModificationEntityChangeEvent {
  return !!changeEvent.lastModifierUser && !!changeEvent.lastModificationTime;
}

function changeEventFromLastModification(
  changeEvent: MayHaveAnyEntityChangeEvent,
  type: 'modified' | 'registered',
): EntityChangeEvent | undefined {
  return isMayHaveLastModifcationEntityChangeEvent(changeEvent)
    ? {
        user: changeEvent.lastModifierUser.fullName,
        time: changeEvent.lastModificationTime,
        type,
      }
    : undefined;
}

interface HasEditingEntityChangeEvent {
  editingUser?: UserCatalogDto;
  editingTime: string;
}

function isHasEditingEntityChangeEvent(
  changeEvent: MayHaveAnyEntityChangeEvent,
): changeEvent is HasEditingEntityChangeEvent {
  return !!changeEvent.editingTime;
}

function changeEventFromEditing(
  changeEvent: MayHaveAnyEntityChangeEvent,
): EntityChangeEvent | undefined {
  return isHasEditingEntityChangeEvent(changeEvent)
    ? {
        user: changeEvent.editingUser?.fullName ?? EntityChangeFallbackName,
        time: changeEvent.editingTime,
        type: 'editing',
      }
    : undefined;
}

interface HasCompletedEntityChangeEvent {
  executionCompletedByUser: UserCatalogDto;
  executionCompleted: string;
}

function isMayHaveCompletedChangeEvent(
  changeEvent: MayHaveAnyEntityChangeEvent,
): changeEvent is HasCompletedEntityChangeEvent {
  return !!changeEvent.executionCompletedByUser && !!changeEvent.executionCompleted;
}

function changeEventFromCompleted(
  changeEvent: MayHaveAnyEntityChangeEvent,
): EntityChangeEvent | undefined {
  return isMayHaveCompletedChangeEvent(changeEvent)
    ? {
        user: changeEvent.executionCompletedByUser.fullName,
        time: changeEvent.executionCompleted,
        type: 'editing',
      }
    : undefined;
}

export type MayHaveAnyEntityChangeEvent = Partial<
  Nullable<
    HasCreatedEntityChangeEvent &
      HasEditingEntityChangeEvent &
      HasDeletionEntityChangeEvent &
      HasCompletedEntityChangeEvent &
      HasLastModificationEntityChangeEvent
  >
>;

export function getEntityChangeEvent(
  entity: MayHaveAnyEntityChangeEvent,
  type: EntityChangeType,
): EntityChangeEvent | undefined {
  switch (type) {
    case 'created':
    case 'ptwCreated':
      return changeEventFromCreated(entity);
    case 'deleted':
      return changeEventFromDeletion(entity);
    case 'modified':
    case 'registered':
      return changeEventFromLastModification(entity, type);
    case 'editing':
      return changeEventFromEditing(entity);
    case 'completed':
      return changeEventFromCompleted(entity);
  }
}
